import type {
  AttachmentModel,
  AttachmentRepr,
} from '@fc-vehicles/service/attachment-model';

// Only image payloads come without `repr`: the backend's `image_envelope`
// (avatars, logos, location/contact/time-clock photos, with a mime type),
// `attachment_envelope` and quick-search photos (no mime type), and locally
// built DVR clip / camera snapshot images. Everything serialized as an
// Attachment or a Drive node carries `repr`.
export function getAttachmentRepr(
  attachment: AttachmentModel | null | undefined,
): AttachmentRepr {
  if (attachment?.repr) return attachment.repr;
  const mimeType = attachment?.mimeType;
  return !attachment || (mimeType && !mimeType.startsWith('image/'))
    ? { type: 'file', media: 'other' }
    : { type: 'image', media: 'thumbnail' };
}

// `media: 'pdf'` names the rendition that suits the type, not one already on
// the row: `viewablePdf` lands from its own task after `processed` flips, so
// until then there is nothing a PDF viewer can read — the original is an
// office file, not a PDF.
export function getAttachmentDocumentSource(
  attachment: AttachmentModel | null | undefined,
): string | null {
  const repr = getAttachmentRepr(attachment);
  if (repr.type !== 'document') return null;
  if (repr.media === 'pdf') return attachment?.viewablePdf ?? null;
  return typeof attachment?.original === 'string' ? attachment.original : null;
}

export function isAttachmentPreviewPending(
  attachment: AttachmentModel | null | undefined,
): boolean {
  const repr = getAttachmentRepr(attachment);
  return (
    repr.type === 'document' && repr.media === 'pdf' && !attachment?.viewablePdf
  );
}

export function isAudioRepr(repr: AttachmentRepr | null | undefined): boolean {
  return repr?.type === 'audio' || repr?.type === 'recording';
}

export function isAudioAttachment(
  attachment: AttachmentModel | null | undefined,
): boolean {
  return isAudioRepr(attachment?.repr);
}

export function isVoiceRecordingAttachment(
  attachment: AttachmentModel | null | undefined,
): boolean {
  return attachment?.repr?.type === 'recording';
}
